import { llmApi } from "@/services/rag-service";
import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Model, PlaygroundParametersProps } from "@/types";
// import { ButtonGroup as ModeSelection } from "../../../components/ui/button-group";
import { ChevronDown, Search } from "lucide-react";
import { IDefaultModel } from "@/types/conversations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMemo, useState } from "react";
import { cn } from "@/utils/cn";
import { SliderWithLabel } from "@/components/ui/slider-with-label";
import { Tooltip } from "@/components/ui/tooltip-card";
import AgentToolsList from "./agent-tools/agent-tools-list";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { PlaygroundParametersMobile } from "./playground-parameters-mobile";
import BlueprintSelect from "./blueprint-select";

/* Mode selection is hidden while Agentic is the only supported mode.
const modes = [
  {
    value: "agentic",
    labelKey: "playground.agentic",
    icon: <Sparkles className="h-3 w-3" />,
    tooltipKey: "playground.agenticTooltip",
  },
];
*/
interface LLMConfigsResponse {
  items: Array<Model>;
  total_count: number;
}
export const PlaygroundParameters = ({
  selectedModel,
  temperature,
  maxTokens,
  onModelChange,
  onTemperatureChange,
  onMaxTokensChange,
  onModelsLoaded,
  mode,
  onModeChange,
  onPlannerModelChange,
  selectedPlannerModel,
  language,
  onLanguageChange,
  leftActions,
  leadingActions,
  onOpenToolsMenu,
  defaultModels,
  blueprintId,
  onBlueprintIdChange,
}: PlaygroundParametersProps) => {
  const { t } = useTranslation();
  const [openChange, setOpenChange] = useState(false);
  const { data: models, isLoading } = useQuery<Array<Model>>({
    queryKey: ["llm-configs"],
    queryFn: () => {
      return llmApi.getLLMConfigs().then((response) => {
        const { items } = response as unknown as LLMConfigsResponse;
        const models =
          items?.filter((model) => !model.is_embedding_model) ?? [];

        return models;
      });
    },
  });

  if (models && onModelsLoaded) {
    onModelsLoaded(models);
  }

  if (isLoading) return <Skeleton />;
  const model = models?.find((model) => model.id === selectedModel);
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <div className="md:hidden">
        <PlaygroundParametersMobile
          models={models}
          selectedModel={selectedModel}
          selectedPlannerModel={selectedPlannerModel}
          temperature={temperature}
          maxTokens={maxTokens}
          mode={mode}
          language={language}
          onModelChange={onModelChange}
          onPlannerModelChange={onPlannerModelChange}
          onModeChange={onModeChange}
          onTemperatureChange={onTemperatureChange}
          onMaxTokensChange={onMaxTokensChange}
          onLanguageChange={onLanguageChange}
          leftActions={leftActions}
          leadingActions={leadingActions}
          onOpenToolsMenu={onOpenToolsMenu}
          defaultModels={defaultModels}
          blueprintId={blueprintId}
          onBlueprintIdChange={onBlueprintIdChange}
        />
      </div>
      <div className="hidden md:flex flex-wrap justify-between items-center gap-y-2">
        <div className="flex items-center gap-2 min-w-0">
          {leadingActions}
          <BlueprintSelect
            value={blueprintId ?? ""}
            onChange={onBlueprintIdChange}
          />
        </div>
        {/*
        <div className="flex items-center gap-2 min-w-0">
          <ModeSelection
            className="w-fit grid-cols-1"
            value={mode}
            onValueChange={onModeChange}
            options={modes.map((mode) => ({
              ...mode,
              label: t(mode.labelKey),
              icon: mode.icon, // Correctly passing the icon property
              tooltip: t(mode.tooltipKey), // Passing the tooltip property
            }))}
          />
        </div>
        */}
        <div className="flex gap-1 sm:gap-2 items-center ms-auto">
          {leftActions}
          <AgentToolsList onOpen={onOpenToolsMenu} />
          <DropdownMenu defaultOpen={openChange} onOpenChange={setOpenChange}>
            <DropdownMenuTrigger asChild>
              <div
                className={cn(
                  "transition-all  cursor-pointer duration-150 rounded-[35px]  pt-2 pr-2 pb-2 pl-3 rtl:pr-3 rtl:pl-2 flex justify-center items-center",
                  {
                    "bg-secondary text-[var(--color-100)] transition-all duration-200":
                      openChange,
                  },
                )}
              >
                <Tooltip content={model?.name}>
                  <p className="max-w-[72px] sm:max-w-[100px] truncate text-muted-foreground text-xs sm:text-sm">
                    {model?.name ?? ""}
                  </p>
                </Tooltip>
                <ChevronDown
                  className="text-[12px] text-muted-foreground"
                  strokeWidth={1.75}
                />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[min(calc(100vw-1rem),224px)] sm:w-56 bg-[var(--card-background)] border-[1px] border-solid border-ring"
              align="end"
              side="bottom"
              sideOffset={6}
              collisionPadding={8}
            >
              <DropdownMenuLabel className="text-left rtl:text-right text-accent-foreground font-bold text-[10px] opacity-55">
                {t("common.model")}
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                <ModelMenuSub
                  type="base"
                  models={models}
                  onModelChange={onModelChange}
                  selectedModel={model}
                  defaultModels={defaultModels}
                />
              </DropdownMenuGroup>

              <DropdownMenuSeparator />
              {/* <DropdownMenuGroup>
                <SliderWithLabel
                  label={t("playground.temperature")}
                  min={0}
                  max={1}
                  step={0.01}
                  value={temperature}
                  onChange={onTemperatureChange}
                />
                <SliderWithLabel
                  label={t("playground.maximumTokens")}
                  min={0}
                  max={model?.max_tokens || 4096}
                  step={Math.max(
                    1,
                    Math.floor((model?.max_tokens || 4096) / 100),
                  )}
                  value={maxTokens}
                  onChange={onMaxTokensChange}
                />
              </DropdownMenuGroup> */}
              <DropdownMenuLabel className="text-left rtl:text-right text-accent-foreground font-bold text-[10px] opacity-55">
                {t("playground.language")}
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                <LanguageMenuSub
                  language={language}
                  onLanguageChange={onLanguageChange}
                />
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Box>
  );
};

const ModelMenuSub = ({
  models,
  selectedModel,
  onModelChange,
  type,
  defaultModels,
}: {
  models?: Array<Model>;
  selectedModel?: Model;
  onModelChange: (modelId: string) => void;
  type: string;
  defaultModels?: IDefaultModel[];
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const defaultModelId = defaultModels?.find((m) =>
    type === "base" ? m.mode === "base" : m.mode === "planner",
  )?.llm_config.id;

  const filteredModels = useMemo(() => {
    if (!models) return [];
    return models.filter((model) => {
      const isSupportedType = model.is_chat_model || model.is_multimodal;

      const isLegacyType =
        !model.is_chat_model &&
        !model.is_multimodal &&
        !model.is_default &&
        !model.is_embedding_model &&
        !model.is_image_generation_model &&
        !model.is_shared &&
        !model.is_stt_model &&
        !model.is_tts_model;

      return isSupportedType || isLegacyType;
    });
  }, [models]);

  const searchedModels = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return filteredModels;
    return filteredModels.filter((model) =>
      model.name.toLocaleLowerCase().includes(query),
    );
  }, [filteredModels, searchQuery]);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {selectedModel?.name ?? ""}
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          className="w-[min(calc(100vw-1rem),306px)] border-[1px] border-solid border-ring bg-[var(--card-background)]"
          onCloseAutoFocus={() => setSearchQuery("")}
        >
          <div
            className="relative mb-1 px-1"
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("playground.searchPlaceholder")}
              aria-label={t("playground.searchPlaceholder")}
              className="h-9 w-full rounded-md border border-border bg-transparent ps-9 pe-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
            />
          </div>
          <div className="max-h-[320px] overflow-y-auto overscroll-contain pe-1 [scrollbar-color:var(--primary)_transparent]">
            {searchedModels.map((model) => (
              <DropdownMenuItem
                key={type + model.id}
                onClick={() => onModelChange(model.id)}
                className="flex min-w-0 items-center justify-between gap-2 rounded-[35px] border border-transparent outline-none data-[highlighted]:border-foreground data-[highlighted]:bg-accent focus:border-foreground focus:bg-accent focus:outline-none focus-visible:outline-none focus-visible:ring-0"
              >
                <span className="min-w-0 flex-1 truncate">{model.name}</span>
                {model.id === defaultModelId && (
                  <span className="bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full font-medium">
                    {t("playground.default")}
                  </span>
                )}
              </DropdownMenuItem>
            ))}
            {searchedModels.length === 0 && (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                {t("usageAndCost.llmConfigs.noModelsFound")}
              </p>
            )}
          </div>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};

const LanguageMenuSub = ({
  language,
  onLanguageChange,
}: {
  language: string;
  onLanguageChange: (language: string) => void;
}) => {
  const { t } = useTranslation();
  const languages = [
    { value: "auto", label: t("playground.auto") },
    { value: "en", label: t("playground.english") },
    { value: "ar", label: t("playground.arabic") },
  ];
  const selectedLanguage =
    languages.find((lang) => lang.value === language) || languages[0];
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{selectedLanguage.label}</DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="border-[1px] border-solid border-ring bg-[var(--card-background)]">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.value}
              onClick={() => onLanguageChange(lang.value)}
            >
              {lang.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};
